import React, { useState } from "react";
import styled from "@emotion/styled";
import { Modal, Col, Row, Button } from "react-bootstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import Loader from "views/universal/Loader/Loader";
import { centimesToCurrency } from "utils/utils";

/**
 * Historique unifié des transactions bancaires d'un client : PayGreen + Edenred.
 * Les deux sources renvoient des lignes au même format, la colonne "Type" indique la provenance.
 * PayGreen vient de son API (seule à connaître les tentatives refusées), Edenred de notre base.
 */

const PERIODS = [
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
  { days: 365, label: "1 an" },
];

const PROVIDERS = [
  { key: "all", label: "Tous" },
  { key: "paygreen", label: "PayGreen" },
  { key: "edenred", label: "Edenred" },
];

/** Vocabulaire commun aux deux connecteurs (voir PaygreenService::normalizeStatus) */
const STATUS_LABELS = {
  SUCCEEDED: { label: "Accepté", variant: "success" },
  CAPTURED: { label: "Encaissé", variant: "success" },
  COMPLETED: { label: "Terminé", variant: "success" },
  AUTHORIZED: { label: "Autorisé", variant: "warning" },
  PENDING: { label: "En attente", variant: "warning" },
  FAILED: { label: "Refusé", variant: "danger" },
  CANCELLED: { label: "Annulé", variant: "neutral" },
  REFUNDED: { label: "Remboursé", variant: "info" },
};

const PopupTransactions = (props) => {
  const {
    show,
    onHide,
    onEscapeKeyDown,
    customer = {},
    transactions: state = {},
    onChangeDays,
  } = props;

  require("moment/locale/fr.js");

  const [provider, setProvider] = useState("all");

  const data = state.data || {};
  const allTransactions = data.transactions || [];
  const days = state.days || 90;
  const paygreen = data.paygreen || {};
  const edenred = data.edenred || {};

  const countByProvider = (key) =>
    key === "all"
      ? allTransactions.length
      : allTransactions.filter((transaction) => transaction.provider === key).length;

  const transactions =
    provider === "all"
      ? allTransactions
      : allTransactions.filter((transaction) => transaction.provider === provider);

  return (
    <Popup
      size="xl"
      show={show}
      onHide={onHide}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      <Row>
        <Col>
          <Title className="text-center">
            <small>{`${customer.lastname || ""} ${customer.firstname || ""}`}</small>
            <br />
            <span>Transactions bancaires</span>
          </Title>
        </Col>
      </Row>

      <Toolbar noGutters>
        <Col xs={12} sm={7}>
          {PERIODS.map((period) => (
            <ToolbarButton
              key={period.days}
              type="button"
              size="sm"
              variant={period.days === days ? "success" : "outline-secondary"}
              disabled={state.isLoading}
              onClick={() => onChangeDays(period.days)}
            >
              {period.label}
            </ToolbarButton>
          ))}
          <ToolbarButton
            type="button"
            size="sm"
            variant="outline-secondary"
            title="Rafraîchir (les transactions PayGreen sont mises en cache 1 minute)"
            disabled={state.isLoading}
            onClick={() => onChangeDays(days, true)}
          >
            <FontAwesomeIcon icon={faSyncAlt} />
          </ToolbarButton>
        </Col>
        <Col xs={12} sm={5} className="text-right">
          {paygreen.profileUrl && (
            <HeaderLink
              href={paygreen.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ouvrir le profil PayGreen{paygreen.sandbox ? " (sandbox)" : ""}
            </HeaderLink>
          )}
          {edenred.username && (
            <HeaderInfo>
              Edenred : {edenred.username} (
              {edenred.accountConnected ? "carte connectée" : "connexion expirée"})
            </HeaderInfo>
          )}
        </Col>
      </Toolbar>

      {state.isLoading ? (
        <Loader items={2} />
      ) : (
        <>
          {state.error && <ErrorBox>{state.error}</ErrorBox>}
          {!state.error && paygreen.error && (
            <WarningBox>
              Transactions PayGreen indisponibles : {paygreen.error}
            </WarningBox>
          )}

          {!state.error && (
            <Filters noGutters>
              <Col>
                {PROVIDERS.map((item) => (
                  <ToolbarButton
                    key={item.key}
                    type="button"
                    size="sm"
                    variant={item.key === provider ? "dark" : "outline-dark"}
                    onClick={() => setProvider(item.key)}
                  >
                    {item.label} ({countByProvider(item.key)})
                  </ToolbarButton>
                ))}
              </Col>
            </Filters>
          )}

          {!state.error && transactions.length === 0 && (
            <Empty>{`Aucune transaction sur les ${days} derniers jours.`}</Empty>
          )}

          {transactions.length > 0 && (
            <>
              <Header noGutters>
                <Col sm={2}><p><b>Date</b></p></Col>
                <Col sm={2}><p><b>Type</b></p></Col>
                <Col sm={2}><p><b>Statut</b></p></Col>
                <Col sm={2}><p><b>Montant</b></p></Col>
                <Col sm={2}><p><b>Commande</b></p></Col>
                <Col sm={2}><p><b>Référence</b></p></Col>
              </Header>

              {transactions.map((transaction, key) => {
                const status = STATUS_LABELS[transaction.status] || {
                  label: transaction.rawStatus || "Inconnu",
                  variant: "neutral",
                };
                const reference =
                  transaction.reference || transaction.paymentOrderId || transaction.id;

                return (
                  <Transaction key={transaction.id || key} noGutters>
                    <Col sm={2}>
                      <p>
                        {transaction.date
                          ? moment(transaction.date).format("DD/MM/YYYY HH:mm")
                          : "Date inconnue"}
                      </p>
                    </Col>
                    <Col sm={2}>
                      <Provider provider={transaction.provider}>
                        {transaction.providerLabel || transaction.provider}
                      </Provider>
                      {transaction.cardBrand && transaction.cardLast4 && (
                        <Detail>
                          {transaction.cardBrand} •••• {transaction.cardLast4}
                        </Detail>
                      )}
                    </Col>
                    <Col sm={2}>
                      <Status variant={status.variant} title={transaction.rawStatus || ""}>
                        {status.label}
                      </Status>
                      {transaction.reason && <Detail>{transaction.reason}</Detail>}
                    </Col>
                    <Col sm={2}>
                      <p>
                        <b>
                          {transaction.amount !== null && transaction.amount !== undefined
                            ? centimesToCurrency(transaction.amount)
                            : "-"}
                        </b>
                      </p>
                    </Col>
                    <Col sm={2}>
                      <p>
                        {transaction.orderId ? (
                          <Link href={transaction.orderLink} target="_blank" rel="noopener noreferrer">
                            {transaction.numberOrder || `Commande ${transaction.orderId}`}
                          </Link>
                        ) : transaction.poolOperation ? (
                          <Muted>Porte-monnaie</Muted>
                        ) : (
                          <Muted>Non rattachée</Muted>
                        )}
                      </p>
                    </Col>
                    <Col sm={2}>
                      <Reference title={reference || ""}>{reference || "-"}</Reference>
                    </Col>
                  </Transaction>
                );
              })}
            </>
          )}
        </>
      )}
    </Popup>
  );
};

const Popup = styled(Modal)`
  .modal-content {
    background-color: #ecf0f5 !important;
  }
  p {
    margin-bottom: 0rem;
  }
`;

const Title = styled.h3`
  span {
    font-weight: bold;
    color: ${(props) => props.theme.colors.green};
  }
`;

const Toolbar = styled(Row)`
  align-items: center;
  margin-bottom: 0.8rem;
`;

const Filters = styled(Row)`
  margin-bottom: 0.6rem;
`;

const ToolbarButton = styled(Button)`
  margin-right: 0.4rem;
`;

const Header = styled(Row)`
  color: grey;
  padding: 0.6rem;
  font-size: 0.8rem;
`;

const Transaction = styled(Row)`
  padding: 0.6rem;
  background-color: white;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  align-items: center;
`;

const STATUS_COLORS = {
  success: "#429853",
  warning: "#d38b00",
  danger: "#c9302c",
  info: "#31708f",
  neutral: "#777",
};

const PROVIDER_COLORS = {
  paygreen: "#1d1d1b",
  edenred: "#0a5ca8",
};

const Status = styled.span`
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 12px;
  font-size: 0.85rem;
  color: white;
  background-color: ${(props) => STATUS_COLORS[props.variant] || STATUS_COLORS.neutral};
`;

const Provider = styled.span`
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  color: white;
  background-color: ${(props) => PROVIDER_COLORS[props.provider] || "#777"};
`;

const Detail = styled.p`
  font-size: 0.75rem;
  color: grey;
`;

const Reference = styled.p`
  font-size: 0.75rem;
  color: grey;
  word-break: break-all;
`;

const Muted = styled.span`
  color: #999;
  font-style: italic;
`;

const Link = styled.a`
  color: ${(props) => props.theme.colors.green};
  text-decoration: underline;
`;

const HeaderLink = styled.a`
  display: block;
  color: ${(props) => props.theme.colors.green};
  text-decoration: underline;
`;

const HeaderInfo = styled.span`
  color: grey;
  font-size: 0.85rem;
`;

const ErrorBox = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  border: solid 1px #f5c6cb;
  border-radius: 4px;
  padding: 0.8rem;
  margin-bottom: 0.5rem;
`;

const WarningBox = styled.div`
  background-color: #fcf8e3;
  color: #8a6d3b;
  border: solid 1px #faebcc;
  border-radius: 4px;
  padding: 0.8rem;
  margin-bottom: 0.5rem;
`;

const Empty = styled.p`
  color: grey;
  font-style: italic;
  text-align: center;
  padding: 1rem 0;
`;

PopupTransactions.propTypes = {};

export default PopupTransactions;
